import { useCallback } from 'react';
import { useLoading } from '@/contexts/LoadingContext';
import { useActionLoading } from './useActionLoading';
import { useAudioLoading } from './useAudioLoading';
import { usePageLoading } from './usePageLoading';

type LoadingPriority = 'audio' | 'action' | 'page' | 'general';

interface LoadingCoordinationOptions {
  priority: LoadingPriority;
  timeout?: number;
  fallbackMessage?: string;
}

export const useLoadingCoordination = () => {
  const { loadingStates, setLoading, clearLoading } = useLoading();
  const { isActionLoading, getActionLoadingMessage } = useActionLoading();
  const { isAudioLoading, getAudioLoadingMessage } = useAudioLoading();
  const { isPageLoading, getPageLoadingMessage } = usePageLoading();

  // Get the highest priority active loading state
  const getActiveLoadingState = useCallback(() => {
    // Priority order: audio > action > page > general
    const priorities: LoadingPriority[] = ['audio', 'action', 'page', 'general'];
    
    for (const priority of priorities) {
      const activeStates = Object.entries(loadingStates).filter(
        ([key, state]) => state.isLoading && state.type === priority
      );
      
      if (activeStates.length > 0) {
        const [key, state] = activeStates[0];
        return {
          key,
          state,
          priority,
          message: state.message
        };
      }
    }
    
    return null;
  }, [loadingStates]);

  // Get coordinated loading message based on priority
  const getCoordinatedMessage = useCallback((fallback = 'Loading...') => {
    const activeState = getActiveLoadingState();
    return activeState?.message || fallback;
  }, [getActiveLoadingState]);

  // Check if any high-priority loading is active
  const hasHighPriorityLoading = useCallback(() => {
    const audioLoading = Object.values(loadingStates).some(
      state => state.isLoading && state.type === 'audio'
    );
    const actionLoading = Object.values(loadingStates).some(
      state => state.isLoading && state.type === 'action'
    );
    
    return audioLoading || actionLoading;
  }, [loadingStates]);

  // Set coordinated loading with proper priority handling
  const setCoordinatedLoading = useCallback((
    key: string,
    loading: boolean,
    options: LoadingCoordinationOptions
  ) => {
    const { priority, timeout, fallbackMessage = 'Loading...' } = options;
    
    // If setting high priority loading, clear lower priority states
    if (loading && (priority === 'audio' || priority === 'action')) {
      // Clear page and general loading states
      Object.keys(loadingStates).forEach(stateKey => {
        const state = loadingStates[stateKey];
        if (state.isLoading && (state.type === 'page' || state.type === 'general')) {
          clearLoading(stateKey);
        }
      });
    }
    
    setLoading(key, loading, fallbackMessage, priority, undefined);
    
    // Set timeout if specified
    if (loading && timeout) {
      setTimeout(() => {
        clearLoading(key);
      }, timeout);
    }
  }, [loadingStates, setLoading, clearLoading]);

  // Check for specific loading states by type
  const getLoadingStatesByType = useCallback((type: LoadingPriority) => {
    return Object.entries(loadingStates).filter(
      ([, state]) => state.isLoading && state.type === type
    );
  }, [loadingStates]);

  // Get loading statistics for debugging
  const getLoadingStats = useCallback(() => {
    const stats = {
      total: 0,
      byType: {} as Record<LoadingPriority, number>
    };
    
    Object.values(loadingStates).forEach(state => {
      if (state.isLoading) {
        stats.total++;
        stats.byType[state.type] = (stats.byType[state.type] || 0) + 1;
      }
    });
    
    return stats;
  }, [loadingStates]);

  return {
    // Main coordination methods
    getActiveLoadingState,
    getCoordinatedMessage,
    setCoordinatedLoading,
    hasHighPriorityLoading,
    
    // Query methods
    getLoadingStatesByType,
    getLoadingStats,
    
    // Individual type checkers
    isAudioLoading,
    isActionLoading,
    isPageLoading,
    
    // Message getters
    getAudioLoadingMessage,
    getActionLoadingMessage,
    getPageLoadingMessage
  };
};